/**
 * The range presets and the day-string arithmetic behind them.
 *
 * A range is two YYYY-MM-DD days, which is the form the reports API takes and
 * the form a date input holds, so nothing converts between them. Days are the
 * viewer's local calendar days: the admin is on campus, and the server reads
 * the same strings as Denver days.
 */

export const PRESETS = Object.freeze([
    { key: 'semester', label: 'This semester' },
    { key: 'year', label: 'Last 12 months' },
    { key: 'all', label: 'All time' },
]);

const pad = (value) => String(value).padStart(2, '0');

export const toDay = (date) =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/**
 * The academic term containing `today`: spring January to May, summer June
 * and July, fall August to December. The terms are calendar months rather
 * than registrar dates, which differ by campus and by year.
 */
function semesterOf(today) {
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    if (month <= 5) {
        return { from: `${year}-01-01`, to: `${year}-05-31` };
    }
    if (month <= 7) {
        return { from: `${year}-06-01`, to: `${year}-07-31` };
    }
    return { from: `${year}-08-01`, to: `${year}-12-31` };
}

/** The `{ from, to }` days a preset stands for. All time is unbounded. */
export function rangeFor(key, today = new Date()) {
    if (key === 'semester') {
        return semesterOf(today);
    }
    if (key === 'year') {
        const start = new Date(today);
        start.setFullYear(start.getFullYear() - 1);
        start.setDate(start.getDate() + 1);
        return { from: toDay(start), to: toDay(today) };
    }
    return { from: '', to: '' };
}

/** True when both bounds are present and the range runs backwards. */
export const isBackwards = ({ from, to }) => Boolean(from && to && from > to);
